import { useCallback } from "react";
import {
  NoteType,
  OutboundStatus,
  ResourceType,
} from "../../../../utils/constants";
import { getLocationFullName } from "../../../../utils/locationUtils";
import { isOutbound } from "../../../../utils/trackCarPersistence";
import { applyInboundSpotPlacement } from "../../../../utils/shipmentPlacement";

/**
 * Manager-side equivalent of crew's updateShipmentOnPlacement
 * (see crewApp/pages/trackManagement/hooks/useTrackLocationOperations.js).
 *
 * Fires when a railcar is spotted onto a physical track (dragged out of the
 * Unspotted bucket). Flips the underlying shipment's status/dates/history:
 *   - inbound IN_TRANSIT/ARRIVED/SIT_PENDING -> PLACED (or SIT_PLACED) + placedDateTime
 *   - outbound PENDING/ARRIVED -> PLACED + placementDateTime (arrivalDateTime backfilled if unset)
 *
 * The shared useTrackOperations.handleTrackDrop only invokes this when the drop
 * target is a real (non-unspotted) track, so un-spotting never changes status.
 *
 * An inbound placement can be refused (no inventory, not SIT) — the car is
 * still spotted, only its shipment stays put. updateShipmentOnPlacement then
 * resolves to {railCarNumber, reason} so the caller can report every refused
 * car of a multi-car drop in one message; it resolves to undefined otherwise.
 */
export const useTrackPlacementUpdate = ({
  tracks = [],
  saveShipment,
  notify,
  logError,
}) => {
  const resolveTrackByLocationId = useCallback(
    (trackLocationId) => {
      if (!trackLocationId) return null;
      return (
        (tracks || []).find(
          (track) => track?.locations?.items?.[0]?.id === trackLocationId,
        ) || null
      );
    },
    [tracks],
  );

  // Updates shipment status/dates/history when a railcar is placed on track.
  // For drag-drop, targetTrackId is the canonical placement context.
  // trackCarShipment is TrackCar from processedCarsMap; extract shipment for API.
  const updateShipmentOnPlacement = useCallback(
    async (trackCarShipment, previousStatus, targetTrackId = null) => {
      if (!trackCarShipment) return;
      const shipment =
        trackCarShipment.inbound ??
        trackCarShipment.outbound ??
        trackCarShipment;
      const isOut = isOutbound(trackCarShipment);
      const trackForLocation = resolveTrackByLocationId(targetTrackId);

      if (targetTrackId && !trackForLocation) {
        logError(
          "trackManagementAdmin:updateShipmentOnPlacement:targetTrackNotFound",
          new Error(`Could not resolve target track ${targetTrackId}`),
          notify,
        );
      }
      const location = trackForLocation?.locations?.items?.[0];
      const locationFullName = location ? getLocationFullName(location) : null;
      const placementHistoryText = locationFullName
        ? `Railcar placed on track ${locationFullName}`
        : "Railcar placed on track";

      if (isOut) {
        const toUpdate = {
          ...shipment,
          parentType: trackCarShipment.parentType ?? ResourceType.OUTBOUND,
          active: shipment.active ?? "true",
        };
        if (toUpdate.status == null) {
          logError(
            "trackManagementAdmin:updateShipmentOnPlacement:missingOutboundStatus",
            new Error("Missing outbound status on root shipment payload"),
            notify,
          );
          return;
        }
        if (
          toUpdate.status === OutboundStatus.PENDING ||
          toUpdate.status === OutboundStatus.ARRIVED
        ) {
          toUpdate.status = OutboundStatus.PLACED;
          // Same reset the dispatch form's save applies when it derives
          // PLACED — a stale REJECTED state or demurrage count must not
          // survive a fresh spotting.
          toUpdate.state = "";
          toUpdate.daysOnDemurrageClock = 0;
          // Spotting is the placement event. arrivalDateTime (gate arrival)
          // is filled only when nothing stamped it — rail has no gate
          // check-in, and it sorts the by-dates list GSIs.
          if (!toUpdate.placementDateTime)
            toUpdate.placementDateTime = new Date().toISOString();
          if (!toUpdate.arrivalDateTime)
            toUpdate.arrivalDateTime = toUpdate.placementDateTime;
        }
        toUpdate.history = [
          ...(toUpdate.history || []),
          {
            type: NoteType.SHIPMENT,
            text: placementHistoryText,
            date: new Date().toISOString(),
            by: window.appUser?.fullName,
          },
        ];
        await saveShipment(toUpdate);
      } else {
        const toUpdate = {
          ...shipment,
          parentType: trackCarShipment.parentType ?? ResourceType.INBOUND,
          railCarNumber:
            shipment.railCarNumber ?? trackCarShipment.railCarNumber ?? "",
        };
        if (toUpdate.status == null || toUpdate.state == null) {
          logError(
            "trackManagementAdmin:updateShipmentOnPlacement:missingInboundStatusState",
            new Error("Missing inbound status/state on root shipment payload"),
            notify,
          );
          return;
        }
        // Status/state advance and the history note both come from the shared
        // helper, which also decides whether placement is refused. The caller
        // reports the refusal — one drop can refuse several cars at once.
        const refusal = applyInboundSpotPlacement(toUpdate, {
          locationFullName,
        });
        await saveShipment(toUpdate);
        if (refusal)
          return { railCarNumber: toUpdate.railCarNumber, reason: refusal };
      }
    },
    [resolveTrackByLocationId, saveShipment, logError, notify],
  );

  return { updateShipmentOnPlacement };
};
